import type { MatrixEvent } from 'matrix-js-sdk'

export interface ThreadSummary {
  /** Number of events in the thread (excluding the root), per the server bundle. */
  count: number
  /** Event id of the latest event in the thread, if known. */
  latestEventId?: string
  /** Timestamp of the latest event in the thread, if known. */
  latestEventTs?: number
  /** Whether the current user participated in the thread, per the server bundle. */
  currentUserParticipated?: boolean
}

export interface SerializedEvent {
  eventId: string
  roomId: string
  type: string
  sender: string
  senderName: string
  senderAvatar: string | null
  timestamp: number
  content: Record<string, unknown>
  stateKey?: string
  unsigned?: Record<string, unknown>
  redacted: boolean
  /** Root event id when this event is a thread reply (rel_type === "m.thread"). */
  threadRootId?: string
  /** True when this event is itself the root of a thread (carries an m.thread bundle). */
  isThreadRoot?: boolean
  /** Aggregated thread summary from the server bundle, present on thread roots. */
  threadSummary?: ThreadSummary
}

const THREAD_REL_TYPE = 'm.thread'

/**
 * Read the m.thread relation from an event's content, if any.
 *
 * A thread reply carries `m.relates_to: { rel_type: "m.thread", event_id }`.
 * We deliberately read from raw content rather than relying on a particular
 * matrix-js-sdk helper so this stays stable across SDK minor versions.
 */
function getThreadRootId(event: MatrixEvent): string | undefined {
  const relatesTo = (event.getContent() as Record<string, unknown>)?.['m.relates_to'] as
    | { rel_type?: string; event_id?: string }
    | undefined
  if (relatesTo?.rel_type === THREAD_REL_TYPE && typeof relatesTo.event_id === 'string') {
    return relatesTo.event_id
  }
  return undefined
}

/**
 * Extract the server-aggregated m.thread bundle from unsigned relations, if the
 * homeserver provided one. Present on thread root events.
 */
function getThreadSummary(event: MatrixEvent): ThreadSummary | undefined {
  const unsigned = event.getUnsigned() as Record<string, unknown> | undefined
  const relations = unsigned?.['m.relations'] as Record<string, unknown> | undefined
  const bundle = relations?.[THREAD_REL_TYPE] as
    | {
        count?: number
        latest_event?: { event_id?: string; origin_server_ts?: number }
        current_user_participated?: boolean
      }
    | undefined
  if (!bundle || typeof bundle.count !== 'number') return undefined
  return {
    count: bundle.count,
    latestEventId: bundle.latest_event?.event_id,
    latestEventTs: bundle.latest_event?.origin_server_ts,
    currentUserParticipated: bundle.current_user_participated,
  }
}

export function serializeEvent(event: MatrixEvent): SerializedEvent {
  const senderId = event.getSender() ?? ''
  const member = event.sender
  const rawName = member?.name || senderId.replace(/^@(.+?):.*$/, '$1')
  const senderName = rawName.replace(/\s*\(@[^)]+\)\s*$/, '')
  const senderAvatar = member?.getMxcAvatarUrl() ?? null

  const threadRootId = getThreadRootId(event)
  const threadSummary = getThreadSummary(event)

  return {
    eventId: event.getId() ?? '',
    roomId: event.getRoomId() ?? '',
    type: event.getType(),
    sender: senderId,
    senderName,
    senderAvatar,
    timestamp: event.getTs(),
    content: event.getContent() as Record<string, unknown>,
    stateKey: event.getStateKey(),
    unsigned: event.getUnsigned() as Record<string, unknown> | undefined,
    redacted: event.isRedacted(),
    ...(threadRootId ? { threadRootId } : {}),
    ...(threadSummary ? { isThreadRoot: true, threadSummary } : {}),
  }
}
